/* PDP gallery video playback.
   Markup ships with preload="none" and no autoplay attribute (see
   product-image-thumbnail.liquid), so no video bytes compete with the page
   load. Two observers then take over, both wired up only after window load so
   they never fight the LCP image:
     - a "warm" observer buffers a video shortly before its slide is reached,
       so arriving at the slide does not land on a bare poster;
     - a "play" observer starts playback once the slide is mostly in view and
       pauses it on the way out, saving battery and bandwidth. */
(function () {
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Shopify generates a video's preview image from frame 0. On clips that open
     on an empty backdrop that frame is blank, so the paused slide reads as
     "nothing loaded". Once buffered, nudge past it to a frame with real
     footage in it. */
  var STILL_FRAME_TIME = 0.4;

  /* Warming asks for metadata, never 'auto'. Measured on a 10.4s 1080p product
     clip: 'metadata' buffers enough to paint the seeked still frame and to
     start playback immediately, while 'auto' fetches the entire file — 3.2MB
     on a page that is otherwise 4.1MB. */
  var WARM_PRELOAD = 'metadata';

  /* A quarter viewport of lead-in, deliberately short. On mobile the gallery is
     a full-width swipe carousel, so the next slide sits a whole screen away and
     is warmed by swipe intent instead (see warmOnGalleryIntent). Desktop stacks
     the slides vertically, where a quarter screen of warning is plenty. */
  var WARM_MARGIN = '25%';

  var warmObserver = null;
  var playObserver = null;

  /* Every video handed to the observers. An IntersectionObserver keeps a strong
     reference to its targets, so the slides a colour switch detaches have to be
     released by hand (see releaseDetachedVideos). */
  var observedVideos = new Set();

  /* Metered or very slow connections: no speculative buffering and no autoplay.
     Leave a control so the shopper can still opt in. */
  function connectionIsFrugal() {
    var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) return false;
    if (connection.saveData) return true;
    return /^(slow-2g|2g)$/.test(connection.effectiveType || '');
  }

  function onPageLoaded(callback) {
    if (document.readyState === 'complete') {
      callback();
      return;
    }
    window.addEventListener('load', callback, { once: true });
  }

  function whenIdle(callback) {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(callback, { timeout: 2000 });
      return;
    }
    setTimeout(callback, 200);
  }

  function seekPastBlankFrame(video) {
    if (video.dataset.galleryVideoSeeked) return;

    /* Guard against a clip shorter than the nudge itself. */
    var duration = video.duration || 0;
    if (!duration) return;
    var target = Math.min(STILL_FRAME_TIME, duration / 4);

    /* Only nudge a still that is sitting inside the blank lead-in: never yank
       a video that is already playing, nor one a shopper has scrubbed on. */
    if (!video.paused || video.currentTime >= target) return;

    video.dataset.galleryVideoSeeked = 'true';
    try {
      video.currentTime = target;
    } catch (error) {
      /* Seeking refused — the poster simply stays put. */
    }
  }

  function warmVideo(video) {
    if (video.dataset.galleryVideoWarmed) return;
    video.dataset.galleryVideoWarmed = 'true';

    video.preload = WARM_PRELOAD;

    /* Already has metadata: load() here would throw the buffer away. */
    if (video.readyState >= 1) {
      seekPastBlankFrame(video);
      return;
    }

    video.addEventListener(
      'loadedmetadata',
      function () {
        seekPastBlankFrame(video);
      },
      { once: true }
    );
    video.load();
  }

  function playVideo(video) {
    warmVideo(video);
    video.muted = true; /* defensive: iOS requires muted for scriptless play */
    var promise = video.play();
    if (promise && promise.catch) {
      /* Autoplay can be blocked (e.g. iOS Low Power Mode) — fall back to
         showing controls so the video is still watchable by tap. */
      promise.catch(function () {
        video.controls = true;
      });
    }
  }

  /* The first sign a shopper is swiping the carousel is enough to start
     buffering: a swipe takes long enough to land that metadata arrives with it,
     and shoppers who never touch the gallery never pay for the video. */
  function warmOnGalleryIntent(video) {
    var gallery = video.closest('.product__image-list');
    if (!gallery) return;

    var events = ['pointerdown', 'touchstart', 'scroll'];
    var onIntent = function () {
      events.forEach(function (name) {
        gallery.removeEventListener(name, onIntent);
      });
      warmVideo(video);
    };

    events.forEach(function (name) {
      gallery.addEventListener(name, onIntent, { passive: true });
    });
  }

  function ensureObservers() {
    if (playObserver) return;

    playObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            playVideo(entry.target);
          } else if (!entry.target.paused) {
            entry.target.pause();
          }
        });
      },
      { threshold: 0.4 }
    );

    warmObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          warmObserver.unobserve(entry.target);
          warmVideo(entry.target);
        });
      },
      { rootMargin: WARM_MARGIN }
    );
  }

  /* pdp-color-switch.js swaps the gallery's innerHTML, which drops the previous
     colour's <video> nodes on the floor still observed and, if one was mid-play,
     still downloading. Detached targets never report an intersection change, so
     the play observer would not pause them either. */
  function releaseDetachedVideos() {
    if (!observedVideos.size) return;

    observedVideos.forEach(function (video) {
      if (document.contains(video)) return;

      observedVideos.delete(video);
      if (playObserver) playObserver.unobserve(video);
      if (warmObserver) warmObserver.unobserve(video);
      try {
        video.pause();
      } catch (error) {
        /* Nothing to pause. */
      }
    });
  }

  function bindVideos() {
    releaseDetachedVideos();

    var videos = document.querySelectorAll('video[data-gallery-video]:not([data-gallery-video-bound])');
    if (!videos.length) return;

    var frugal = connectionIsFrugal();

    videos.forEach(function (video) {
      video.setAttribute('data-gallery-video-bound', 'true');

      if (reducedMotion || frugal) {
        /* No autoplay and no speculative fetch — just a control to tap. The
           poster carries the slide until then. */
        video.controls = true;
        return;
      }

      if (!('IntersectionObserver' in window)) {
        onPageLoaded(function () {
          playVideo(video);
        });
        return;
      }

      ensureObservers();
      observedVideos.add(video);
      onPageLoaded(function () {
        playObserver.observe(video);
        whenIdle(function () {
          warmObserver.observe(video);
          warmOnGalleryIntent(video);
        });
      });
    });
  }

  bindVideos();
  document.addEventListener('shopify:section:load', bindVideos);

  /* An instant colour switch replaces the gallery's markup without a section
     load event, so the sibling colour's slides arrive unbound: preload="none",
     no autoplay attribute and nothing observing them, i.e. a video that just
     sits on its poster. pdp-color-switch.js calls this after the swap.
     Idempotent - already-bound videos carry data-gallery-video-bound. */
  window.mnBindPdpGalleryVideos = bindVideos;
})();
